import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Calendar, Video, CreditCard, Activity, 
  RefreshCw, Lock, Filter, Search, Settings, Package, 
  ShoppingCart, DollarSign, BookOpen, ChevronDown, Menu
} from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  StatCard, 
  UserRow, 
  UserDetailModal, 
  AvailabilityManager, 
  AdminCalendar,
  PlansManager,
  InventoryManager,
  SalesManager,
  FinanceDashboard,
  SystemManual,
  STATUS_CONFIG 
} from '../components/admin';

// Configuración de navegación agrupada
const NAV_GROUPS = [
  {
    id: 'gestion',
    label: 'Gestión',
    icon: Users,
    items: [
      { id: 'users', label: 'Usuarios', icon: Users },
      { id: 'calendar', label: 'Calendario', icon: Calendar },
      { id: 'availability', label: 'Disponibilidad', icon: Lock },
      { id: 'plans', label: 'Planes', icon: Settings },
    ]
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    icon: ShoppingCart,
    items: [
      { id: 'inventory', label: 'Stock', icon: Package },
      { id: 'sales', label: 'Ventas', icon: ShoppingCart },
      { id: 'finance', label: 'Finanzas', icon: DollarSign },
    ]
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: Activity,
    items: [
      { id: 'activity', label: 'Actividad', icon: Activity },
      { id: 'transactions', label: 'Transacciones', icon: CreditCard },
      { id: 'manual', label: 'Manual', icon: BookOpen },
    ]
  }
];

// Componente de navegación móvil con dropdown
const MobileNav = ({ activeTab, onTabChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const getCurrentTabInfo = () => {
    for (const group of NAV_GROUPS) {
      const item = group.items.find(i => i.id === activeTab);
      if (item) return { group: group.label, item: item.label, icon: item.icon };
    }
    return { group: 'Gestión', item: 'Usuarios', icon: Users };
  };

  const currentTab = getCurrentTabInfo();
  const CurrentIcon = currentTab.icon;

  return (
    <div className="lg:hidden mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl"
      >
        <div className="flex items-center gap-3">
          <CurrentIcon className="h-5 w-5 text-primary" />
          <div className="text-left">
            <p className="text-xs text-muted-foreground">{currentTab.group}</p>
            <p className="font-medium">{currentTab.item}</p>
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="mt-2 bg-background border border-white/10 rounded-xl overflow-hidden shadow-xl">
          {NAV_GROUPS.map((group) => (
            <div key={group.id}>
              <button
                onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 border-b border-white/5"
              >
                <div className="flex items-center gap-3">
                  <group.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">{group.label}</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${expandedGroup === group.id ? 'rotate-180' : ''}`} />
              </button>
              
              {expandedGroup === group.id && (
                <div className="bg-white/5">
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        onTabChange(item.id);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 p-4 pl-12 text-left transition-colors min-h-[48px] ${
                        activeTab === item.id 
                          ? 'bg-primary/20 text-primary border-l-2 border-primary' 
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="text-sm">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente de navegación desktop con sidebar
const DesktopNav = ({ activeTab, onTabChange }) => {
  return (
    <div className="hidden lg:block w-56 flex-shrink-0">
      <div className="sticky top-24 space-y-2">
        {NAV_GROUPS.map((group) => (
          <div key={group.id} className="mb-6">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-3">
              {group.label}
            </h3>
            <div className="space-y-1">
              {group.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
                    activeTab === item.id 
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Admin = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [statusStats, setStatusStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const includeDeleted = showDeleted || statusFilter === 'eliminado';
      const statusParam = statusFilter !== 'all' ? `&status_filter=${statusFilter}` : '';
      
      const [dashboardRes, usersRes, logsRes, transactionsRes, statusStatsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get(`/admin/users?include_deleted=${includeDeleted}${statusParam}`),
        api.get('/admin/logs?limit=50'),
        api.get('/admin/transactions?limit=50'),
        api.get('/admin/stats/by-status')
      ]);
      setDashboard(dashboardRes.data);
      setUsers(usersRes.data);
      setLogs(logsRes.data);
      setTransactions(transactionsRes.data);
      setStatusStats(statusStatsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de administración');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [statusFilter, showDeleted]);

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Render del contenido según tab activa
  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <CardTitle className="text-lg sm:text-xl">Gestión de Usuarios</CardTitle>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px] px-3 py-2 bg-white/5 rounded-lg">
                    <input 
                      type="checkbox" 
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                      className="rounded w-4 h-4"
                    />
                    <span className="whitespace-nowrap">Mostrar eliminados</span>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Buscar usuario..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full sm:w-64 min-h-[44px]"
                    />
                  </div>
                </div>
              </div>
              {statusFilter !== 'all' && (
                <div className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
                  <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">
                    Filtrando: <span className={`${STATUS_CONFIG[statusFilter]?.color} px-2 py-0.5 rounded ml-1`}>
                      {STATUS_CONFIG[statusFilter]?.label}
                    </span>
                  </span>
                  <button 
                    onClick={() => setStatusFilter('all')} 
                    className="text-xs text-primary hover:underline ml-auto min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    Limpiar
                  </button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredUsers.map((u) => (
                  <UserRow key={u.id} user={u} onSelect={setSelectedUser} />
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-center text-muted-foreground py-12">No se encontraron usuarios</p>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 'calendar':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Calendar className="h-5 w-5" />
                Calendario de Reservas
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <div className="min-w-[320px]">
                <AdminCalendar />
              </div>
            </CardContent>
          </Card>
        );

      case 'availability':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Lock className="h-5 w-5" />
                Gestión de Disponibilidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AvailabilityManager />
            </CardContent>
          </Card>
        );

      case 'plans':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Settings className="h-5 w-5" />
                Configuración de Planes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PlansManager />
            </CardContent>
          </Card>
        );

      case 'inventory':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Package className="h-5 w-5" />
                Control de Stock
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InventoryManager />
            </CardContent>
          </Card>
        );

      case 'sales':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <ShoppingCart className="h-5 w-5" />
                Punto de Venta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SalesManager />
            </CardContent>
          </Card>
        );

      case 'finance':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <DollarSign className="h-5 w-5" />
                Dashboard Financiero
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FinanceDashboard />
            </CardContent>
          </Card>
        );

      case 'activity':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Actividad Reciente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {logs.map((log, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4 bg-white/5 rounded-lg">
                    <Activity className="h-4 w-4 text-primary flex-shrink-0 hidden sm:block" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{log.action}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {log.details ? JSON.stringify(log.details).slice(0, 60) : 'Sin detalles'}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('es-AR')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'transactions':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Transacciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {transactions.map((t, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-white/5 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{t.description}</p>
                      <p className="text-xs text-muted-foreground">{t.type}</p>
                    </div>
                    <div className="text-left sm:text-right flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                      <p className={`font-bold text-sm ${t.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {t.amount >= 0 ? '+' : ''}{t.amount} créditos
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'manual':
        return (
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <BookOpen className="h-5 w-5" />
                Manual del Sistema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SystemManual />
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold uppercase tracking-tight">
              Panel de Administración
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Control total del sistema</p>
          </div>
          <Button 
            onClick={fetchData} 
            variant="outline" 
            className="gap-2 min-h-[44px] w-full sm:w-auto rounded-lg"
          >
            <RefreshCw className="h-4 w-4" /> 
            <span>Actualizar</span>
          </Button>
        </div>

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <StatCard title="Usuarios" value={dashboard?.stats.total_users || 0} icon={Users} />
          <StatCard title="Reservas" value={dashboard?.stats.total_bookings || 0} icon={Calendar} />
          <StatCard title="Análisis" value={dashboard?.stats.total_analyses || 0} icon={Video} />
          <StatCard title="Transacciones" value={dashboard?.stats.total_transactions || 0} icon={CreditCard} />
          <StatCard title="Reservas Hoy" value={dashboard?.stats.bookings_today || 0} icon={Activity} className="col-span-2 sm:col-span-1" />
        </div>

        {/* Status Stats - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={key} 
                className={`glass-effect border-white/10 cursor-pointer transition-all active:scale-95 ${
                  statusFilter === key ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              >
                <CardContent className="p-3 sm:p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Icon className={`h-4 w-4 ${config.color.replace('bg-', 'text-').replace('/20', '')}`} />
                    <p className={`text-xl sm:text-2xl font-bold ${config.color.replace('bg-', 'text-').replace('/20', '')}`}>
                      {statusStats[key] || 0}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Users by Plan - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <Card className="glass-effect border-white/10">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-gray-400">{dashboard?.users_by_plan.none || 0}</p>
              <p className="text-xs text-muted-foreground">Sin Plan</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-white/10">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-blue-400">{dashboard?.users_by_plan.inicial || 0}</p>
              <p className="text-xs text-muted-foreground">Plan Inicial</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-white/10">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-green-400">{dashboard?.users_by_plan.basic || 0}</p>
              <p className="text-xs text-muted-foreground">Plan Basic</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-white/10">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-purple-400">{dashboard?.users_by_plan.socio || 0}</p>
              <p className="text-xs text-muted-foreground">Plan Socio</p>
            </CardContent>
          </Card>
        </div>

        {/* Layout Principal - Sidebar + Content */}
        <div className="flex gap-8">
          {/* Desktop Sidebar Navigation */}
          <DesktopNav activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Mobile Navigation */}
            <MobileNav activeTab={activeTab} onTabChange={setActiveTab} />

            {/* Tab Content */}
            {renderContent()}
          </div>
        </div>

        {/* User Detail Modal */}
        {selectedUser && (
          <UserDetailModal 
            user={selectedUser} 
            onClose={() => setSelectedUser(null)}
            onRefresh={() => {
              fetchData();
              setSelectedUser(null);
            }}
          />
        )}
      </div>
    </div>
  );
};
